/**
 * Calling from the app (1.5.7). Staff call tenants from their own mobiles, so each person chooses how the
 * app's tap-to-call links dial for them:
 *
 *   normal — the link as it always was (`tel:+1…`);
 *   hide   — "Hide my number when I call from the app": `*67` and then the number, so the tenant's phone
 *            shows "No Caller ID" for that call (a US carrier feature, per call);
 *   teams  — "Call through Teams": the number opens in Teams. Offered, and honoured, only while the
 *            administrator has "Teams Phone is set up" on (Admin → Settings → System); otherwise a normal call.
 *
 * Every call link the signed-in app shows is built here (src/lib/calling.ts reads the person's choice). The
 * signed-out link pages are not staff calls and do not use it. No dependencies: the app and the API share it.
 */

export const CALL_MODES = ['normal', 'hide', 'teams'] as const;
export type CallMode = (typeof CALL_MODES)[number];

/** The SM_Settings row behind "Teams Phone is set up" (Yes / No; No until the administrator says otherwise). */
export const TEAMS_PHONE_KEY = 'TEAMS_PHONE_ENABLED';

/** What GET /api/me says about calling: the person's choice as it applies now, and whether Teams is offered. */
export interface CallingSettings {
  mode: CallMode;
  teams: boolean;
}

export function isCallMode(v: unknown): v is CallMode {
  return typeof v === 'string' && (CALL_MODES as readonly string[]).includes(v);
}

/**
 * 1.8.3 (D-1.8.3-1): how a person's call links dial while they have not chosen. A crew member calls a tenant from
 * their own mobile at the door, so a crew account starts with "Hide my number when I call from the app" (*67);
 * everyone else starts with a normal call. A saved choice always wins — this is only what stands in for none.
 */
export function defaultCallMode(role: string | null | undefined): CallMode {
  return role === 'crew' ? 'hide' : 'normal';
}

/** Teams is only a choice while Teams Phone is set up; without it the call is a normal one. */
export function effectiveCallMode(mode: CallMode | null | undefined, teamsEnabled: boolean): CallMode {
  if (mode === 'hide') return 'hide';
  return mode === 'teams' && teamsEnabled ? 'teams' : 'normal';
}

/** The ten digits of a US number ("(212) 555-0101", "+1 212…", "1-212…"), or null when it is not one. */
function tenDigits(phone: string): string | null {
  const d = phone.replace(/\D/g, '');
  if (d.length === 10) return d;
  if (d.length === 11 && d.startsWith('1')) return d.slice(1);
  return null;
}

/** The number as a dialler takes it: +1 and ten digits for a US number, else as written (digits, a leading +). */
function dialled(phone: string): string {
  const us = tenDigits(phone);
  if (us) return `+1${us}`;
  return phone.replace(/[^\d+]/g, '');
}

export interface CallLink {
  href: string;
  /** How the call is really placed (Teams falls back to normal while Teams Phone is not set up). */
  via: CallMode;
}

/** The link for a phone number, for a person's choice. */
export function callLink(phone: string, mode: CallMode = 'normal', teamsEnabled = false): CallLink {
  const via = effectiveCallMode(mode, teamsEnabled);
  const number = dialled(phone);
  if (via === 'teams') return { href: `msteams://teams.microsoft.com/l/call/0/0?users=4:${number}`, via };
  if (via === 'hide') {
    // *67 goes before the national number (no +1, or the carrier does not take the code). A number from
    // another country is dialled the way a US phone dials it: 011 and the digits.
    const us = tenDigits(phone);
    const rest = us ?? (number.startsWith('+') ? `011${number.slice(1)}` : number);
    return { href: `tel:*67${rest}`, via };
  }
  return { href: `tel:${number}`, via };
}

/** `callLink(...).href` — what goes in an `<a href>`. */
export function callHref(phone: string, mode: CallMode = 'normal', teamsEnabled = false): string {
  return callLink(phone, mode, teamsEnabled).href;
}
